import type { XmlNode } from './xml-parse';
import { parseXmlTree, TEXT_NODE } from './xml-parse';

/**
 * Navigation helpers for the KML, KMZ and GPX parsers.
 *
 * Everything here goes through `localName` rather than a tag name or a CSS
 * selector. KML declares a default namespace and uses a `gx:` prefix for the
 * interesting parts, and GPX files in the wild carry three or four namespace
 * variants of the same element. Matching on local name reads all of them and
 * cannot be broken by a prefix someone chose differently.
 */

export type { XmlNode };
export { parseXmlTree, TEXT_NODE };

/** @throws ParseError when the text contains no XML at all. */
export function parseXml(text: string): XmlNode {
  return parseXmlTree(text);
}

/** Direct child elements with this local name. */
export function childrenNamed(parent: XmlNode, localName: string): XmlNode[] {
  return parent.children.filter((child) => child.localName === localName);
}

/** The first direct child element with this local name. */
export function childNamed(parent: XmlNode, localName: string): XmlNode | undefined {
  return parent.children.find((child) => child.localName === localName);
}

/** Every descendant element with this local name, in document order. */
export function descendantsNamed(root: XmlNode, localName: string): XmlNode[] {
  const found: XmlNode[] = [];
  // Iterative rather than recursive: a deeply nested document should not be
  // able to overflow the stack on a pilot's file.
  const pending: XmlNode[] = [root];
  while (pending.length > 0) {
    const node = pending.pop();
    if (node === undefined) {
      continue;
    }
    if (node !== root && node.localName === localName) {
      found.push(node);
    }
    // Reversed, so popping walks children in document order.
    for (let i = node.children.length - 1; i >= 0; i -= 1) {
      const child = node.children[i];
      if (child !== undefined) {
        pending.push(child);
      }
    }
  }
  return found;
}

/** The first descendant element with this local name, in document order. */
export function descendantNamed(root: XmlNode, localName: string): XmlNode | undefined {
  for (const child of root.children) {
    if (child.localName === localName) {
      return child;
    }
    const found = descendantNamed(child, localName);
    if (found !== undefined) {
      return found;
    }
  }
  return undefined;
}

/** An attribute's value, or `undefined`. */
export function attributeOf(node: XmlNode | undefined, name: string): string | undefined {
  return node?.attributes[name];
}

/**
 * All text inside an element and its descendants, untrimmed and in order.
 *
 * Whitespace-only runs are not stored, so an element containing nothing but
 * indentation reads as `''` rather than as that indentation. Every caller here
 * trims anyway.
 */
export function textContentOf(node: XmlNode | undefined): string {
  if (node === undefined) {
    return '';
  }
  if (node.localName === TEXT_NODE) {
    return node.text;
  }
  let text = '';
  for (const child of node.children) {
    text += textContentOf(child);
  }
  return text;
}

/** Trimmed text content of an element, or `''`. */
export function textOf(node: XmlNode | undefined): string {
  return textContentOf(node).trim();
}

const HTML_ENTITIES: Readonly<Record<string, string>> = {
  '&deg;': '°',
  '&nbsp;': ' ',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
};

/**
 * Reduce a snippet of HTML to its readable text.
 *
 * Flightradar24 hides the per-fix altitude, speed and heading inside an HTML
 * `<description>` block, so reading those values means reading that markup.
 * The entities survive as literal text because the block arrives inside CDATA,
 * where an XML parser leaves them alone by definition.
 */
export function htmlToText(html: string): string {
  let text = html.replace(/<[^>]*>/g, ' ');
  for (const [entity, char] of Object.entries(HTML_ENTITIES)) {
    text = text.split(entity).join(char);
  }
  return text.replace(/\s+/g, ' ').trim();
}
